import { Link } from "react-router-dom";
import {
  Hammer, ArrowRight, GitBranch, Package, Cpu, Terminal, ShieldAlert, Sparkles, Boxes,
} from "lucide-react";

const FEATURES = [
  { icon: GitBranch, title: "Import from anywhere", body: "Connect GitHub or drop a ZIP — Kailash walks the tree, detects your stack, and builds a project profile you can trust." },
  { icon: Package,   title: "10 Linux distros",     body: "Ubuntu, Debian, Mint, Kali, Fedora, Rocky, AlmaLinux, openSUSE, Arch, Generic — with the package format that fits each family." },
  { icon: Cpu,       title: "x86_64 & ARM64",       body: "Choose the arch. Kailash tailors dependencies, install paths and system libraries accordingly." },
  { icon: Terminal,  title: "Real, runnable config",body: "Claude Sonnet 4.5 emits actual build scripts, control files, spec files, PKGBUILDs, systemd units, .desktop launchers, and Dockerfiles — not marketing." },
  { icon: ShieldAlert, title: "Honest state machine", body: "Configured → Building → Build Successful → Packaged → Verified. We label AI-generated config exactly for what it is. Nothing is faked." },
  { icon: Boxes,     title: "Knuth ↔ Kailash",       body: "Share a project between the two workbenches. Knuth documents it. Kailash releases it." },
];

const WORKFLOW = [
  { step: "01", title: "Import",    body: "Push a repo, drop a ZIP, or point at a public Git URL. Kailash indexes the source and detects the stack." },
  { step: "02", title: "Pick targets", body: "Choose distro + version + architecture + package format. Every combination is sanity-checked." },
  { step: "03", title: "Generate",  body: "Claude Sonnet 4.5 writes the build scripts, packaging metadata and install steps for that exact combination." },
  { step: "04", title: "Ship",      body: "Download the generated config, run it in your isolated builder, and produce a real signed artifact." },
];

export default function KailashLanding() {
  return (
    <div className="container-narrow py-12 md:py-16" data-testid="kailash-landing">
      <section className="grid lg:grid-cols-5 gap-10 items-start mb-20">
        <div className="lg:col-span-3">
          <div className="label-eyebrow mb-3 inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#8DA3B0" }} />
            An app from Durga Programming Adventures
          </div>
          <h1 className="font-heading text-5xl md:text-7xl font-semibold leading-[1.02] mb-5">
            <span className="italic text-marigold">Kailash</span> turns source
            <br />into <span className="italic">Linux releases</span>.
          </h1>
          <p className="text-lg text-ink2 leading-relaxed max-w-xl mb-8">
            Kailash is an AI software build, packaging and release workbench. Import a repository,
            pick your Linux target, and get the exact configuration a real builder needs to ship —
            .deb, .rpm, PKGBUILD, AppImage, Docker.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/kailash" className="btn-primary" data-testid="kailash-launch">
              <Hammer className="w-4 h-4" /> Open Kailash <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/kailash/new" className="btn-outline-ink text-sm" data-testid="kailash-new-project">
              Start a new project
            </Link>
          </div>
          <p className="mt-4 text-xs text-ink2">
            Named after <span className="italic">Mount Kailash</span> — the summit every trekker respects. Because shipping software is a mountain, too.
          </p>
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-3xl border-2 border-ink bg-ink text-cream p-6 shadow-[0_20px_50px_-15px_rgba(26,27,65,0.55)]" data-testid="kailash-hero-card">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2.5 h-2.5 rounded-full bg-marigold" />
              <span className="w-2.5 h-2.5 rounded-full bg-saffron" />
              <span className="w-2.5 h-2.5 rounded-full bg-teal" />
              <span className="ml-3 text-xs font-mono text-cream/60">kailash · build</span>
            </div>
            <div className="font-mono text-[13px] leading-relaxed space-y-1.5">
              <div className="text-teal">$ kailash target ubuntu-24.04-arm64</div>
              <div className="text-cream/70">→ detected: FastAPI · MongoDB · uv</div>
              <div className="text-cream/70">→ resolving system deps · libssl3, python3</div>
              <div className="text-cream/70">→ generating debian/control</div>
              <div className="text-cream/70">→ generating postinst · service unit</div>
              <div className="text-cream/70">→ generating packaging command</div>
              <div className="text-marigold">✓ Configured · ready for builder</div>
              <div className="text-cream/40 text-[11px] mt-2">Configuration only. No binaries produced in this environment.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mb-20">
        <div className="text-center mb-10">
          <div className="label-eyebrow mb-3">What it does</div>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold">Six honest capabilities.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="p-6 rounded-2xl border border-border bg-cream" data-testid={`kailash-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: "#8DA3B022", color: "#526972" }}>
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-1">{f.title}</h3>
              <p className="text-sm text-ink2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section className="mb-20">
        <div className="label-eyebrow mb-3 text-center">How it flows</div>
        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-center mb-10">Four steps to a real release.</h2>
        <div className="grid md:grid-cols-4 gap-4">
          {WORKFLOW.map((w) => (
            <div key={w.step} className="p-6 rounded-2xl border-2 border-ink bg-cream shadow-[6px_6px_0_0_#1A1B41]">
              <div className="text-xs font-mono text-marigold mb-2">{w.step}</div>
              <h3 className="font-heading font-semibold text-lg mb-1">{w.title}</h3>
              <p className="text-sm text-ink2 leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-6 p-8 rounded-3xl bg-ink text-cream text-center">
        <div className="label-eyebrow mb-3 text-cream/70 inline-flex items-center gap-2"><Sparkles className="w-3 h-3" /> Kailash is in beta</div>
        <h3 className="font-heading text-3xl md:text-4xl font-semibold mb-4">Bring us a project.</h3>
        <p className="text-cream/70 max-w-lg mx-auto mb-6">Import your repo, pick a distro, and see what Claude Sonnet 4.5 generates. Nothing runs on our servers — you take the config to your own builder.</p>
        <Link to="/kailash/new" className="btn-primary" data-testid="kailash-cta-new">
          <Hammer className="w-4 h-4" /> Start a project <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </section>
    </div>
  );
}
